import store from '@/store';

/* eslint-disable no-case-declarations */
import {
  IRouterMiddleware,
  MiddlewareRoute,
  MiddlewareCallback
} from '../middleware.interface';
import MerchantService from '../../api/MerchantService';
import {
  getOauthState,
  hasManagerCredentials,
  isExternalOauthSession,
  setExternalOauthSession,
  setOauthState
} from '../router-utils';
import ManagerService from '../../api/ManagerService';
import { IBusiness } from '../../store/modules/managerTypes';
import { merchantoauthSessionKeys } from '../../constants/merchantoauth';
import BusinessService from '../../api/BusinessService';
import { getSessionSignup, setSessionSignup } from '@/views/Signup/session';
import { insideProviderIframe } from '../../utils/providerIframe';
import { saveIframeData } from '../../utils/merchantoauth';
import {
  loadAccountFormData,
  loadCoinStoreData,
  loadStoreDetailsData,
  loadYourStoreData
} from '@/store/modules/businessOnboarding';
import { IBusinessType } from '@/models/business';
import { ERewardsProgramStepSlug } from '@/componentsV2/templates/Modals/RewardsProgramModal/RewardsProgramModal.vue';

import { app } from '@/main';
import { isOnlineProvider } from '@/utils/provider';
import { getSignUpUrl } from '@/views/Onboarding/helpers';

class OauthInstance {
  merchantService = new MerchantService();
  managerService = new ManagerService();
  businessService = new BusinessService();
  query: Record<string, string>;

  constructor(
    private readonly to: MiddlewareRoute,
    private readonly from: MiddlewareRoute,
    private readonly next: MiddlewareCallback
  ) {
    const oauthState = getOauthState();

    if (to.query) {
      if (!to.query.jwt && oauthState && oauthState.query) {
        this.query = oauthState.query;
      }

      this.query = to.query as Record<string, string>;
    } else {
      this.query = {};
    }
  }

  async execute() {
    const { providers } = merchantoauthSessionKeys.connect;
    const query = this.query;

    if (query.cb === 'signup') {
      query.cb = 'external';
    }

    if (query.cb === 'external') {
      if (this.query.cb === 'external')
        app.$store.dispatch('businessOnboarding/RESET_BUSINESS_ONBOARDING');

      setExternalOauthSession(
        isOnlineProvider(query.providerType)
          ? IBusinessType.ONLINE
          : IBusinessType.INSTORE
      );
    }

    if (query.jwt) {
      await this.saveOauthState();
    }

    const state = getOauthState();
    const location =
      (state && state.businessData && state.businessData.locations[0]) || {};
    const signUpSession = getSessionSignup();

    const BIData = {
      step: 'oauth-callback',
      country: location.countryIso2,
      provider: state && state.provider ? state.provider.name : '',
      cb: state && state.query ? state.query.cb : query.cb
    };

    if (insideProviderIframe()) {
      const onboardingOnlinePage = getSignUpUrl(true);

      saveIframeData(query);
      if (
        Object.keys(providers).includes(query.providerType) &&
        !hasManagerCredentials() &&
        !query.connecting
      ) {
        return this.next(
          `${onboardingOnlinePage}?providerName=${
            query.providerType
          }&providerId=${query.providerId}`
        );
      }
    }

    switch (query.cb) {
      case 'connect':
        if (query.error) {
          this.next({ path: '/home', query });
          return;
        }

        if (this.isStartGuide()) {
          await this.redirectStartGuide();
        } else {
          await this.redirectConnection();
        }

        break;
      case 'external':
        const firstScreen = getSignUpUrl(true);

        if (hasManagerCredentials()) {
          const businesses = await this.getBusinessList();
          const oauthState = getOauthState();
          let countryUnavailable = false;
          if (
            oauthState.businessData.locations.length === 0 ||
            !oauthState.businessData.locations[0].country
          ) {
            countryUnavailable = true;
            setOauthState({
              ...oauthState,
              countryUnavailable
            });
          }
          if (businesses.length > 0) {
            this.next(`/business-onboarding/select-business`);
          } else {
            this.next(firstScreen);
            return;
          }
        } else {
          this.next(firstScreen);
          return;
        }
        break;
      case 'update':
        this.next('/');
        break;
      default:
        this.next('/');
    }
  }

  private async getBusinessList(): Promise<IBusiness[]> {
    const { data } = await this.managerService.getBusinessList();
    return data;
  }

  private async getCurrentBusiness() {
    const { data } = await this.businessService.getBusinessInfo();
    return data;
  }

  private isStartGuide() {
    return localStorage.getItem('@twism:star-guide-integration');
  }

  private async redirectStartGuide() {
    localStorage.removeItem('@twism:star-guide-integration');

    const oauthState = getOauthState();
    app.$notification(
      `${oauthState.provider.displayName} connected.`,
      'success'
    );

    window.location.href = `${
      window.location.origin
    }/home?modal=rewardsProgramModal&initialStep=${
      ERewardsProgramStepSlug.REWARD_RATIO
    }`;
  }

  private async redirectConnection() {
    const oauthState = getOauthState();
    const business = await this.getCurrentBusiness();
    await this.merchantService.signupConnectBusiness({
      oauthToken: oauthState.query.jwt,
      businessId: business.id,
      type: business.type
    });
    sessionStorage.setItem(
      merchantoauthSessionKeys.connect.success,
      `${oauthState.provider.displayName} connected.`
    );
    this.next('/settings/apps');
  }

  private async saveOauthState() {
    const response = await this.merchantService.getBusinessSignup(
      this.query.jwt
    );

    const { businessData, provider, rawData = {} } = response.data;

    let successRedirect;

    if (this.query.cb === 'external' && provider.name === 'bigcommerce') {
      successRedirect = '/merchant-connect/finish';
    }

    setOauthState({
      provider,
      businessData,
      query: this.query,
      successRedirect,
      rawData
    });

    if (businessData.locations.length) {
      setSessionSignup({
        country: rawData.country_name || businessData.locations[0].country,
        countryIso2:
          rawData.country_code || businessData.locations[0].countryIso2
      });
    }

    if (isExternalOauthSession()) {
      loadCoinStoreData(store);
      loadStoreDetailsData(store);
      loadYourStoreData(store);
      loadAccountFormData(store);
    }
  }
}

export class HandleOauthCallback implements IRouterMiddleware {
  // private managerService = new ManagerService();
  private routes = new Set(['merchant-connect-oauth']);

  canActivate(to: MiddlewareRoute, from: MiddlewareRoute): boolean {
    return to.name ? this.routes.has(to.name) : false;
  }

  async execute(
    to: MiddlewareRoute,
    from: MiddlewareRoute,
    next: MiddlewareCallback
  ): Promise<void> {
    const instance = new OauthInstance(to, from, next);
    try {
      await instance.execute();
    } catch {
      next('/');
    }
  }
}
